import classNames from 'classnames';
import { routerActions } from 'connected-react-router';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';
import { NavLink } from 'react-router-dom';
import { ConnectionDownLinkIcon, ConnectionUpLinkIcon, selectCall, selectEndpoint, selectLine, webrtcActions, WebRtcClient } from 'webrtc-client-janus';
import { redirectToLogout } from '..';
import { getAssistance, getTemporaryPasswordForWebRtc, openAdministration, openReports, parseJson } from '../api';
import { IpcRendererEvent, IpcRenderOn, IpcRenderRemove } from '../Electron/ipcRenderer';
import { useIsSoftphoneEnabled, usePresence } from '../app-hooks';
import { HasClassName } from '../component-utils';
import { distinct } from '../data-utils';
import { useInterval, useSelector } from '../hook-utils';
import * as legacy from '../legacy';
import { teamsOpenForwardReplayDialog } from '../legacy';
import { CallAssistant, CallState, ClientName, ContactType, DeviceGroupType, EntityType, Presence, QueueEmail } from '../model';
import { actions } from '../store';
import { setPresence } from '../store/actions';
import { selectActiveDevice } from '../store/reducers';
import { selectMissedCallsCount } from '../store/reducers/calls';
import { selectApplicationSetting, selectAwayFromDeskTimeoutSeconds, selectConfigProp, selectDefaultWorkTime, selectInstantMessagerSettings, selectIsSingleQueueOrSquad, selectIsWebRtcEnabled, selectMyAccess, selectMyData, selectMyself, selectShowShortcutsToolbar, selectWecRtcSettings } from '../store/reducers/config';
import { getInstantMessageCounts, getInstantMessageNotification } from '../store/reducers/instantmessager';
import { selectClientLogging, selectCompactView, selectFixedQueueGridHeights, selectInIpfxClient, selectLastAgentView, selectSecondaryRingerDevice, selectShowQueueAgents, selectShowQueuingMedia } from '../store/reducers/localSettings';
import { selectMyMedia } from '../store/reducers/media';
import { selectExtension } from '../store/reducers/queueAgents';
import { selectMyQueueEmails } from '../store/reducers/queueEmails';
import { selectShortcuts } from '../store/reducers/shortcuts';
import { selectUnreadVmCount } from '../store/reducers/voiceMail';
import { Theme, useTeams } from '../TeamsProvider';
import { ExtensionMenuItems } from './AgentMenu';
import { PresenceInfo } from './AgentsTable';
import { getContactIcon } from './AgentView/ContactPage';
import { ContactMenuItems } from './ContactsPage';
import DeviceSelection from './DeviceSelection';
import DropdownButton from './DropdownButton';
import { ReactComponent as BookitIcon } from './icons/bookit.svg';
import { ReactComponent as ContactIcon } from './icons/facebook-user.svg';
import { ReactComponent as FlagIcon } from './icons/flag.svg';
import { ReactComponent as ShortcutIcon } from './icons/folder-shortcuts.svg';
import { ReactComponent as CheckIcon } from './icons/menu-check.svg';
import { ReactComponent as ShowAgentsIcon } from './icons/show-agents.svg';
import { ReactComponent as ShowMediaIcon } from './icons/show-media.svg';
import { ReactComponent as AdminIcon } from './icons/toolbar-admin.svg';
import { ReactComponent as AgentIcon } from './icons/toolbar-agent.svg';
import { ReactComponent as CallAssistantIcon } from './icons/toolbar-callassistant.svg';
import { ReactComponent as CallsIcon } from './icons/toolbar-calls.svg';
import { ReactComponent as DevicesIcon } from './icons/toolbar-devices.svg';
import { ReactComponent as InstantMessageIcon } from './icons/toolbar-instantmessager.svg';
import { ReactComponent as LogoutIcon } from './icons/toolbar-logout.svg';
import { ReactComponent as NewEmailIcon } from './icons/toolbar-new_email.svg';
import { ReactComponent as QueueViewIcon } from './icons/toolbar-queue.svg';
import { ReactComponent as ReportsIcon } from './icons/toolbar-reports.svg';
import { ReactComponent as SearchIcon } from './icons/toolbar-search.svg';
import { ReactComponent as SettingsIcon } from './icons/toolbar-settings.svg';
import { ReactComponent as ThemeIcon } from './icons/toolbar-theme.svg';
import { ReactComponent as VoicemailIcon } from './icons/toolbar-voicemail.svg';
import { ReactComponent as WorktimeIcon } from './icons/toolbar-worktime.svg';
import { ReactComponent as Dynamics365Icon } from './icons/crm/dynamics365.svg';
import { ReactComponent as VoicemailNew } from './icons/voicemail/voicemail_new.svg'
import { ReactComponent as PresenceAwayTimeout } from './icons/presence/presence-settings-away-timeout.svg'
import './MainToolbar.scss';
import DialButton from './MainToolbarDialButton';
import { Menu, MenuDivider, MenuItem, Section, SubMenu } from './Menu';
import PresenceButton from './PresenceButton';
import PresenceIcon from './PresenceIcon';
import ProfilePhoto from './ProfilePhoto';
import QueueIcon from './QueueIcon';
import { QueueMenuItems } from './QueueMenu';
import { DirectoryDropdownItems, QueueRegionDropdownItems } from './RegionDropdownItems';
import SplitButton from './SplitButton';
import Toggle from './Toggle';
import { selectCrmSettings } from '../store/reducers/crmIntegration';
import { openWindowCrmLink } from './CrmIntegrationView';

interface Props extends HasClassName {
}

export default function MainToolbar({ className }: Props) {
  const access = useSelector(selectMyAccess)
  const myId = useSelector(selectMyself)
  const myData = useSelector(selectMyData)
  const extension = useSelector(s => myId && selectExtension(s, myId))
  const activeDevice = useSelector(selectActiveDevice)
  const settings = useSelector(selectInstantMessagerSettings)
  const enabled = settings && settings.imEnabled
  const webRtcEnabled = useSelector(selectIsWebRtcEnabled)
  const dispatch = useDispatch()
  const lastValue = useRef<Boolean>()
  const showShortcutsToolbar = useSelector(selectShowShortcutsToolbar)

  useEffect(() => {
    if (enabled !== null) {
      if (lastValue.current === undefined) {
        if (enabled != null) {
          lastValue.current = enabled
        }
      } else if (lastValue.current !== enabled) {
        legacy.showAlertDialog(ClientName, '<p>A refresh of the ' +
          ClientName +
          ' is required for some settings to take effect.</p>' +
          '<p>If you are currently using a desktop client then you\'ll need to manually restart that too.</p>', legacy.AlertIconType.Info, () => window.location.reload())

        lastValue.current = enabled
      }
    }
  }, [enabled])

  useEffect(() => {
    if (access.isTeamsPbx && myId && extension) {
      if (!access.features.phoneDial && extension.deviceGroupType !== DeviceGroupType.Other) {
        dispatch(actions.setFeaturePhoneDial(true))
        legacy.teamsEnabledPhoneDial(true)
      } else if (access.features.phoneDial && extension.deviceGroupType === DeviceGroupType.Other) {
        dispatch(actions.setFeaturePhoneDial(false))
        legacy.teamsEnabledPhoneDial(false)
      }

      if (!access.features.phoneBusySetCallback && extension.deviceGroupType !== DeviceGroupType.Other) {
        dispatch(actions.setFeaturePhoneBusySetCallback(true))
        legacy.teamsEnabledPhoneBusySetCallback(true)
      } else if (access.features.phoneBusySetCallback && extension.deviceGroupType === DeviceGroupType.Other) {
        dispatch(actions.setFeaturePhoneBusySetCallback(false))
        legacy.teamsEnabledPhoneBusySetCallback(false)
      }

      if (extension.deviceGroupType === DeviceGroupType.Desk && extension.presence !== Presence.Available && extension.presence !== Presence.Queue && webRtcEnabled) {
        if (extension.presence !== Presence.WorkTime) {
          setPresence(myId, Presence.Available)
        }
      }
    }

  }, [access.features.phoneBusySetCallback, access.features.phoneDial, access.isTeamsPbx, dispatch, extension, myId, webRtcEnabled]);

  useEffect(() => {
    const result = (event: IpcRendererEvent, arg: string) => {
      console.log('event ', event, arg)
      if (extension === null || extension === undefined || activeDevice === null) {
        console.warn('unable to perform smart search at the moment')
        return
      }

      dispatch(actions.openSmartSearch('dial',
        extension.status,
        extension.subStatus,
        activeDevice.number,
        false,
        null,
        arg))
    }

    IpcRenderOn('open-smart-search', result)

    return () => {
      IpcRenderRemove('open-smart-search', result)
    }
  }, [activeDevice, dispatch, extension]);

  if (!myId || !extension || !settings || !myData) { return null }

  return <div className={classNames('MainToolbar', className)}>
    {access.features.toolbarQueues && <QueuesButton />}
    <AgentButton />
    {(access.features.toolbarDirectory || (access.features.companyContactsAccess || access.features.directoryCompanyContactAdd || access.features.directoryPersonalContactAdd)) && <ContactsButton />}
    {(access.features.toolBarVoicemail || myData.canReceiveVoicemail) && <VoiceMailButton dropdown={true} />}
    {settings.imEnabled && (access.features.extensionPSChat || access.features.extensionPSChatExternal) && <InstantMessageButton />}
    {access.features.toolbarCallHistory && <CallsButton />}
    {access.features.advancedSearch && <SearchButton />}
    {(!access.isTeamsPbx || (access.isTeamsPbx && extension.deviceGroupType !== DeviceGroupType.Other)) && access.features.toolBarCollaboration && <BookitButton />}
    <div className='separator' />

    {!access.isTeamsPbx && access.features.extensionMyLocation && <PresenceButton text={true} onMore={() => dispatch(actions.openSetPresence(myId))} onSettings={() => legacy.showPresenceSettingsDialog(myId)} />}
    {access.features.workTime && <WorkTimeButton />}

    {access.features.phoneDial && access.features.phoneAnswer && access.features.phoneHangup && <DialButton />}

    {access.features.extensionShortCuts && !showShortcutsToolbar && <ShortcutsButton />}

    {access.isTeamsPbx && <TeamsQueuePresence />}

    <NewEmailButton />

    <div className='spring' />

    <SoftphoneButton />
    <SettingsButton />
  </div>
}

export function QueuesButton() {
  const { replace } = useHistory()
  const isQueuesActive = useRouteMatch('/queues')
  const isSingle = useSelector(selectIsSingleQueueOrSquad)

  function onViewQueues() {
    replace('/queues')
  }

  if (isSingle) {
    return <NavLink title='View queue' replace to='/queues' className='view-switch'>
      <QueueViewIcon />
    </NavLink>
  }

  return <SplitButton onClick={onViewQueues}
    title='View queues' className={classNames('view-switch', isQueuesActive && 'active')}
    items={<QueueRegionDropdownItems onClick={onViewQueues} />}>
    <QueueViewIcon />
  </SplitButton>
}


export function ContactsButton() {
  const { replace } = useHistory()
  const isActive = useRouteMatch('/directory')

  function onView() {
    replace('/directory')
  }

  return <SplitButton onClick={onView}
    title='View directory' className={classNames('view-switch', isActive && 'active')}
    items={<DirectoryDropdownItems onClick={onView} />}>
    <ContactIcon />
  </SplitButton>
}

function SettingsButton() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const access = useSelector(selectMyAccess)
  const myself = useSelector(selectMyself)
  const myData = useSelector(selectMyData)
  const extension = useSelector(s => myself && selectExtension(s, myself))
  const ipfxClient = useSelector(selectInIpfxClient)
  const dispatch = useDispatch()
  const webRtcEnabled = useSelector(s => selectApplicationSetting(s, 'WebRtcEnabled'))
  const version = useSelector(s => selectConfigProp(s, 'version'))
  const autoLocnExpiry = useSelector(s => selectConfigProp(s, 'autoLocnExpiry'))
  const { inTeams } = useTeams()
  const { presence } = usePresence(myself)
  const showShortcutsToolbar = useSelector(selectShowShortcutsToolbar)

  if (!myData || !myself || !presence) return null

  return <button title={'Client settings\r\n\r\n' + myData.fullName + ' [' + myData.extension + ']\r\nVersion [' + version + ']'} ref={ref} onClick={() => setOpen(x => !x)} className='settings'>
    {!inTeams ? access.isTeamsPbx ? <PresenceIcon presence={presence.presence} deviceGroupType={presence.type} awayFromDesk={extension?.awayFromDesk} /> : <ProfilePhoto transparent className='photo' extension={myself} name={myData.fullName} datetime={myData.imageDateTime} /> : <SettingsIcon />}

    <Menu className='SettingsMenu' open={open} onClose={() => setOpen(false)} target={ref.current} placement='bottom-end'>
      <Section title={'Queues'} />
      {access.features.toolbarQueues && <ShowQueueAgentsButton />}
      {access.features.toolbarQueues && <ShowQueuingMediaButton />}
      {access.features.toolbarQueues && <ShowQueueGridFixedButton />}
      <CompactViewButton />
      <VitrualQueueButton />
      {access.features.phoneAnswer && <OpenAgentViewOnAnswer />}
      {access.features.phoneAnswer && <AutoAnswerButton />}
      {access.features.crmIntegration && <OpenCrmButton />}
      <MenuDivider />

      {autoLocnExpiry === true && !access.isTeamsPbx && access.features.extensionMyLocation && <ExtensionPresenceExpiryButton />}

      {access.isTeamsPbx && <TeamsPbxWebRtc />}
      {access.isTeamsPbx && <TeamsPbxEnhanced />}
      {webRtcEnabled && webRtcEnabled.value !== '-1' && <DirectoryWebRtc />}

      {(access.features.extensionPSChat || access.features.extensionPSChatExternal) && <MenuDivider />}
      {(access.features.extensionPSChat || access.features.extensionPSChatExternal) && <InstantMessageEnabled />}
      {(access.features.extensionPSChat || access.features.extensionPSChatExternal) && <InstantMessageSuppressWithPresence />}
      {(access.features.extensionPSChat || access.features.extensionPSChatExternal) && <InstantMessageSendEmailWhenUnavaliable />}

      <MenuDivider />

      {access.features.extensionSetDeviceGroups && <MenuItem onClick={() => legacy.openDeviceGroupsManager(myself)}>
        <DevicesIcon />
        Devices
      </MenuItem>}

      {access.features.reports && <MenuItem onClick={() => openReports()}>
        <ReportsIcon />
        Reports
      </MenuItem>}

      {access.features.toolbarConfiguration && <MenuItem onClick={() => openAdministration()}>
        <AdminIcon />
        Administration
      </MenuItem>}

      {access.features.extensionShortCuts && <MenuItem itemClass='large-svg' onClick={() => legacy.showTeamsOpenShortCutsWindow()}>
        <ShortcutIcon />
        Shortcuts
      </MenuItem>}

      {access.features.extensionShortCuts && <Toggle value={showShortcutsToolbar} onChange={c => dispatch(actions.setShowShortcutsToolbar(c))} >
        Shortcut Toolbar
      </Toggle>}

      {(access.features.licensedEmpower || !access.isLyncLicensed) && access.features.callAssistant && <MenuItem itemClass='large-svg' onClick={() => {
        legacy.teamsOpenCallAssistantDialog((data) => {
          //data is in uppercase
          const d = parseJson<CallAssistant[]>(JSON.stringify(data))
          dispatch(actions.updateCallAssistants(d))
        })
      }}>
        <CallAssistantIcon />
        Call Assistant
      </MenuItem>}

      {access.features.toolbarAssistance && <MenuItem itemClass='large-svg' onClick={() => getAssistance(myself.id)}>
        <FlagIcon />
        Assistance
      </MenuItem>}
      <AwayFromDeskTimeoutSettings />
      {ipfxClient && <MenuDivider />}
      {ipfxClient && <ClientLogging />}

      <MenuDivider />
      <ThemeButton />
      <MenuItem onClick={() => redirectToLogout()}>
        <LogoutIcon />
        Logout
      </MenuItem>
    </Menu>
  </button>
}

function WorkTimeButton() {
  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))
  const dispatch = useDispatch()
  const defaultWorkTime = useSelector(selectDefaultWorkTime)

  const setWorkTime = useCallback((value?: number) => {
    if (value) {
      dispatch(actions.setWorkTime(value))
    } else {
      legacy.showWorkTimeDialogOverride()
    }
  }, [dispatch])

  const [rest, setRest] = useState<number | null>(null)
  const [colour, setColour] = useState('')
  const retTime = extension && extension.presence === Presence.WorkTime && extension.returnTime

  const updateRest = useCallback(() => {
    let colour = ''

    if (retTime) {
      const secs = Math.ceil(dayjs(retTime).diff(Date.now(), 's', true))
      setRest(Math.ceil(secs / 5) * 5)
      if (secs <= 0) {
        // don't
      } else if (secs <= 5) {
        colour = 'WorkTimeButton-red'
      } else if (secs <= 10) {
        colour = 'WorkTimeButton-orange'
      }
    }
    setColour(colour)
  }, [retTime])

  useEffect(() => {
    updateRest()
  }, [updateRest])

  useInterval(updateRest, retTime ? 1000 : null)

  if (!extension || !defaultWorkTime) return null
  return <SplitButton className='WorkTimeButton' onClick={() => {
    if (extension.presence === Presence.WorkTime) {
      setWorkTime(-1)
    } else {
      setWorkTime(defaultWorkTime)
    }
  }} title='Set your work time' items={WorkTimes.map((t, i) => React.cloneElement(renderWorkTimeItem(t, setWorkTime), { key: i }))}>
    <WorktimeIcon className={colour} />
    <div>
      {retTime && rest && rest > 0 && <>
        {rest <= 55 ? `${rest} Sec` : `${Math.ceil(rest / 60)} Min`}
        <br />
      </> || null}
      Work time
    </div>
  </SplitButton>
}

const WorkTimes = [-1, null, 30, 1 * 60, 2 * 60, 3 * 60, 4 * 60, 5 * 60, null, undefined]

function renderWorkTimeItem(item: typeof WorkTimes[0], setTime: (t?: number) => void) {
  if (item === null) {
    return <MenuDivider />
  }

  const onClick = () => setTime(item)

  if (item === undefined) {
    return <MenuItem onClick={onClick}>Custom work time</MenuItem>
  } else if (item === -1) {
    return <MenuItem onClick={onClick}>Clear work time</MenuItem>
  } else if (item < 60) {
    return <MenuItem onClick={onClick}>Add {item} seconds</MenuItem>
  } else if (item === 60) {
    return <MenuItem onClick={onClick}>Add 1 minute</MenuItem>
  } else {
    return <MenuItem onClick={onClick}>Add {item / 60} minutes</MenuItem>
  }
}

function ShortcutsButton() {
  const dispatch = useDispatch()
  const shortcuts = useSelector(selectShortcuts)

  const items = useMemo(() => shortcuts.map(s => {
    switch (s.entityType) {
      case EntityType.Extension:
        return <MenuItem key={s.groupListId} subMenu={<ExtensionMenuItems key={'e-sub' + s.id} extension={s}>
          {children => <SubMenu>{children}</SubMenu>}
        </ExtensionMenuItems>}>
          <PresenceInfo extension={s} enabled={true} />{s.name}
        </MenuItem>

      case EntityType.Queue: {
        return <MenuItem key={s.groupListId} onClick={() => {
          dispatch(routerActions.replace('/queues'))
          dispatch(actions.setQueueView({ region: undefined, queue: { id: s.id, server: s.server } }))
        }} subMenu={< SubMenu >
          <QueueMenuItems key={'q-sub-' + s.id} queue={s} />
        </SubMenu >}>
          <QueueIcon icon={s.icon} />{s.description}
        </MenuItem >
      }

      case EntityType.Contact: {
        const subMenu = <SubMenu key={'sub-c-' + s.id} >
          <ContactMenuItems item={s} onOpen={(item) => {
            dispatch(routerActions.replace('/directory'))
            dispatch(actions.setDirectoryView({ regionId: item.regionId, entityType: EntityType.Contact, contactType: item.extensionNumber === '-1' ? ContactType.Customer : item.extensionNumber === '0' ? ContactType.Company : ContactType.Personal }))
            dispatch(actions.setSelectedContact(item))
          }} onNew={(item) => {
            dispatch(routerActions.replace('/directory'))
            dispatch(actions.setDirectoryView({ regionId: item.regionId, entityType: EntityType.Contact, contactType: item.extensionNumber === '-1' ? ContactType.Customer : item.extensionNumber === '0' ? ContactType.Company : ContactType.Personal }))
            dispatch(actions.setSelectedContact({ id: '', server: '' }))
          }} />
        </SubMenu>

        return <MenuItem key={s.groupListId} subMenu={subMenu}>
          {getContactIcon(s.extensionNumber)}{s.description}
        </MenuItem>
      }

      default: {
        const exhaustive: never = s
        return exhaustive
      }
    }
  }), [dispatch, shortcuts])

  return <>
    <DropdownButton title='Show Shortcuts' className='ShortCutButton' items={<>
      {items}
    </>} caret={false}>
      <ShortcutIcon />
      Shortcuts
    </DropdownButton>
  </>
}

function NewEmailButton() {
  const emails = useSelector(selectMyQueueEmails)
  const myself = useSelector(selectMyself)
  const dispatch = useDispatch()

  useEffect(() => {
    if (emails) return
    dispatch(actions.fetchMyQueueEmails.request())
  }, [dispatch, emails])


  const items = useMemo(() => {
    if (!emails) return []

    const queues = distinct(emails.map(e => e.queueId))
    const items = []

    for (const queue of queues) {
      items.push(<div key={queue} className='queueName'>{queue}{' '}{emails.find(e => e.queueId === queue)!.queueDescription}</div>)

      items.push(...emails.filter(e => e.queueId === queue).map((e, index) => <MenuItem key={`${queue}-${index}`} onClick={() => onClick(e)}>
        {e.emailAddress}
      </MenuItem>))
    }

    return items

    function onClick(e: QueueEmail) {
      if (!myself) { return }
      dispatch(actions.requestNewOutgoing(e, myself))
    }
  }, [dispatch, emails, myself])

  if (!emails || emails.length === 0) {
    return null
  }

  return <DropdownButton title='Send new email from queue' menuClassName='NewEmailButtonMenu' items={items} caret={false}>
    <NewEmailIcon />
    Queue email
  </DropdownButton>
}

function InstantMessageButton() {
  const unread = useSelector(getInstantMessageNotification)
  const counts = useSelector(getInstantMessageCounts)

  return <NavLink title={getMessageAltText(counts.normal, counts.urgent, EntityType.InstantMessage)} replace to='/chat' className='view-switch'>
    <div className='countParent'>
      <InstantMessageIcon />
      {unread.unread > 0 ? <div className='count'>{unread.unread}</div> : null}
    </div>
  </NavLink>
}

function AgentButton() {
  const unread = useSelector(selectMyMedia).filter(m => m.status === CallState.WaitingForWrapup || m.status === CallState.None).length || 0
  const lastAgentView = useSelector(selectLastAgentView)
  const dispatch = useDispatch()

  return <NavLink title='View active media' replace to='/agent' className='view-switch' onClick={e => {
    e.preventDefault()
    dispatch(actions.openAgentView(lastAgentView.mediaId, lastAgentView.tab))
  }}>
    <div className='countParent'>
      <AgentIcon />
      {unread ? <div className='count'>{unread}</div> : null}
    </div>
  </NavLink>
}

function getMessageAltText(normal: number, urgent: number, type: EntityType) {
  let alt = '';
  if (urgent > 0) {
    alt += urgent + ((type === EntityType.Voicemail) ? ' new urgent message' : ' urgent unread conversation') + ((urgent > 1) ? 's' : '');
    if (normal > 0) {
      alt += ' and ';
    }
  }
  if (normal > 0) {
    alt += normal + ((type === EntityType.Voicemail) ? ' new normal message' : ' unread normal conversation') + ((normal > 1) ? 's ' : '');
  }
  if (alt === '') {
    alt = ((type === EntityType.Voicemail) ? 'View Voicemail Message' : 'View Instant Message Conversations');
  }
  return alt;
}

export function VoiceMailButton({ dropdown }: { dropdown: boolean }) {
  const { replace } = useHistory()
  const isActive = useRouteMatch('/vm')

  function onView() {
    replace('/vm')
  }
  const me = useSelector(selectMyself)
  const unread = useSelector(s => me && selectUnreadVmCount(s, me)) || 0

  if (!me) return null

  //need to get urgent sorted
  return dropdown ? <SplitButton onClick={onView}
    title={getMessageAltText(unread, 0, EntityType.Voicemail)} className={classNames('view-switch', isActive && 'active')}
    items={<MenuItem onClick={() => teamsOpenForwardReplayDialog('', 'SEND_NEW', me.id, '', me.id, me.server)}>
      <VoicemailNew />
      New
    </MenuItem>}>
    <div className='countParent'>
      <VoicemailIcon />
      {unread ? <div className='count'>{unread}</div> : null}
    </div>
  </SplitButton> :
    <NavLink title={getMessageAltText(unread, 0, EntityType.Voicemail)} replace to='/vm' className={classNames('view-switch', isActive && 'active')}>
      <div className='countParent'>
        <VoicemailIcon />
        {unread ? <div className='count'>{unread}</div> : null}
      </div>
    </NavLink>
}


export function CallsButton() {
  const unread = useSelector(selectMissedCallsCount) || 0

  return <NavLink title='View recent media' replace to='/calls' className='view-switch'>
    <div className='countParent'>
      <CallsIcon />
      {unread ? <div className='count'>{unread}</div> : null}
    </div>
  </NavLink>
}

function SearchButton() {
  return <NavLink className='view-switch' title='Search for media' replace to='/search'>
    <SearchIcon />
  </NavLink>
}

function BookitButton() {
  const dispatch = useDispatch()
  return <button className='view-switch' title='Book-It' onClick={() => dispatch(actions.requestOpenBookIt())}>
    <BookitIcon />
  </button>
}

type user = {
  password: string
  username: string
  turnPassword: string
  turnUsername: string
  rest: boolean
}

function SoftphoneButton() {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const myself = useSelector(selectMyself)
  const [details, setDetails] = useState<user | undefined>(undefined)
  const settings = useSelector(selectWecRtcSettings)
  const access = useSelector(selectMyAccess)
  const extension = useSelector(s => myself && selectExtension(s, myself))
  const [phoneColour, setPhoneColour] = useState('red')
  const isSoftphoneEnabled = useIsSoftphoneEnabled()
  const endpoint = useSelector(selectEndpoint)
  const line = useSelector(selectLine)
  const call = useSelector(selectCall)
  const wasEnabled = useRef(false)
  const [spamClickCount, setSpamClickCount] = useState(0)
  const [devMode, setDevMode] = useState(false)
  const secondaryRingerDevice = useSelector(selectSecondaryRingerDevice)

  useEffect(() => {
    console.info(`Setting WebRTC softphone secondary ringer to [${secondaryRingerDevice.deviceId}]`)
    dispatch(webrtcActions.configureSecondaryRinger(secondaryRingerDevice.deviceId))
  }, [dispatch, secondaryRingerDevice.deviceId])

  async function ConnectNow() {
    setDetails(undefined)
    const p = await getTemporaryPasswordForWebRtc()
    if (p.success) {
      setDetails({
        password: p.data.ipfx.password,
        username: p.data.ipfx.username,
        rest: p.data.rest,
        turnPassword: p.data.janus.password,
        turnUsername: p.data.janus.username,
      })
    }
  }

  useEffect(() => {
    const webRtcEnabled = isSoftphoneEnabled
    if (wasEnabled.current === webRtcEnabled) { return }

    // we don't need mounted flags because this effect is guarded by the above statement
    wasEnabled.current = webRtcEnabled
    if (!webRtcEnabled) {
      setDetails(undefined)
      return
    }

    ConnectNow()
  }, [isSoftphoneEnabled])

  useEffect(() => {
    if (!isSoftphoneEnabled) return

    let intervalId: NodeJS.Timeout
    if (endpoint.status === 'online' && line.status === 'registered') {
      setPhoneColour('white')
      setSpamClickCount(0)
    } else if (!endpoint.statusError && !line.statusError && (endpoint.status !== 'offline' || line.status === 'registering' || line.status === 'unregistering')) {
      setPhoneColour('orange')
      setSpamClickCount(0)
    } else {
      setPhoneColour('red')
      setDetails(undefined)
      intervalId = setTimeout(async () => {
        const p = await getTemporaryPasswordForWebRtc()
        if (p.success) {
          setDetails({
            password: p.data.ipfx.password,
            username: p.data.ipfx.username,
            rest: p.data.rest,
            turnPassword: p.data.janus.password,
            turnUsername: p.data.janus.username,
          })
        }
      }, 30000)
    }

    return () => {
      if (intervalId != null) {
        clearTimeout(intervalId)
      }
    }
  }, [line.status, endpoint.status, line.statusError, endpoint.statusError, isSoftphoneEnabled, devMode])

  const webrtc = useMemo(() => settings && <WebRtcClient className={classNames('webRtcClient', !(devMode && wasEnabled.current && open) && 'hide')}
    enabled={wasEnabled.current}
    extension={details ? details.username : undefined}
    pin={details ? details.password : undefined}
    settings={{
      ...settings,
      sipDomain: window.location.hostname,

      serverUsername: details && details.turnUsername !== '' ? details.turnUsername : undefined,
      serverPassword: details && details.turnPassword !== '' ? details.turnPassword : undefined,

      turnUsername: details && details.rest && details.turnUsername !== '' ? details.turnUsername : undefined,
      turnPassword: details && details.rest && details.turnPassword !== '' ? details.turnPassword : undefined,
    }}
  />, [details, devMode, open, settings])

  if (!myself || !extension || !settings || !access) return null

  return <>
    {isSoftphoneEnabled && <button title='Softphone' onClick={() => {
      if (spamClickCount > 2 && !devMode) {
        setDevMode(true)
      } else {
        setSpamClickCount(x => x + 1)
      }

      if (devMode) {
        setOpen(x => !x)
      }

      if (endpoint.status === 'online' && line.status === 'registered' ||
        !endpoint.statusError && !line.statusError && (endpoint.status !== 'offline' || line.status === 'registering' || line.status === 'unregistering')) {
        // do nothing
      } else {
        ConnectNow()
      }
    }} className={classNames('phone', 'connectionStatusOverlay', open && 'active')}>
      {call != null && <div className={classNames('connectionStatusContainer')}>
        {call.isSlowAudioUplink && <ConnectionUpLinkIcon className={classNames('connectionIcon', 'uplink')} />}
        {call.isSlowAudioDownlink && <ConnectionDownLinkIcon className={classNames('connectionIcon', 'downlink')} />}
      </div>}
      <svg viewBox='0 0 1792 1792'>
        <path fill={phoneColour} d='M1600 1240q0 27-10 70.5t-21 68.5q-21 50-122 106-94 51-186 51-27 0-52.5-3.5t-57.5-12.5-47.5-14.5-55.5-20.5-49-18q-98-35-175-83-128-79-264.5-215.5t-215.5-264.5q-48-77-83-175-3-9-18-49t-20.5-55.5-14.5-47.5-12.5-57.5-3.5-52.5q0-92 51-186 56-101 106-122 25-11 68.5-21t70.5-10q14 0 21 3 18 6 53 76 11 19 30 54t35 63.5 31 53.5q3 4 17.5 25t21.5 35.5 7 28.5q0 20-28.5 50t-62 55-62 53-28.5 46q0 9 5 22.5t8.5 20.5 14 24 11.5 19q76 137 174 235t235 174q2 1 19 11.5t24 14 20.5 8.5 22.5 5q18 0 46-28.5t53-62 55-62 50-28.5q14 0 28.5 7t35.5 21.5 25 17.5q25 15 53.5 31t63.5 35 54 30q70 35 76 53 3 7 3 21z' />
      </svg>
    </button>}
    {webrtc}
  </>
}

export function TeamsQueuePresence() {
  const myself = useSelector(selectMyself)
  const { presence, setPresence } = usePresence(myself)
  const dispatch = useDispatch()
  const access = useSelector(selectMyAccess)

  const setWorkTime = useCallback((value?: number) => {
    if (value) {
      dispatch(actions.setWorkTime(value))
    } else {
      legacy.showWorkTimeDialogOverride()
    }
  }, [dispatch])

  //Special case for customers that use work time but disable the worktime button! (The login will then just cancel worktime)
  return <Toggle disabled={presence.presence === Presence.WorkTime && access.features.workTime} value={presence.presence === Presence.Queue || (presence.presence === Presence.WorkTime && !access.features.workTime)} onChange={c => {
    if (c) {
      setPresence(Presence.Queue)
    } else if (presence.presence === Presence.WorkTime && !access.features.workTime) {
      setWorkTime(-1)
    } else {
      setPresence(Presence.Available)
    }
  }}>
    Login
  </Toggle>
}


function InstantMessageEnabled() {
  const settings = useSelector(selectInstantMessagerSettings)
  const dispatch = useDispatch()

  if (!settings) return null

  return <Toggle value={settings.imEnabled} onChange={c => dispatch(actions.instantMessagerSettingsSet(c, undefined, undefined))}>
    Instant Message Enable
  </Toggle>
}

function DirectoryWebRtc() {
  const dispatch = useDispatch()
  const state = useSelector(s => selectApplicationSetting(s, 'WebRtcEnabled'))

  if (!state || (state && state.value === '-1')) return null

  return <>
    <Toggle value={state.value === '1'} onChange={c => dispatch(actions.setWebRtcEnabled(c))} >
      Enable Softphone
    </Toggle>
    {state.value === '1' && <DeviceSelection />}
  </>
}

function TeamsPbxWebRtc() {
  const dispatch = useDispatch()
  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))

  if (!extension) return null

  return <Toggle value={extension.deviceGroupType === DeviceGroupType.Other} onChange={c => dispatch(actions.teamsToggleDeviceGroup(c, { id: extension.id, server: extension.server }))}>
    Deliver Via Teams
  </Toggle>
}

function TeamsPbxEnhanced() {
  const myId = useSelector(selectMyself)
  const access = useSelector(selectMyAccess)
  const myTeamsEnhancedCti = useSelector(s => selectConfigProp(s, 'teamsEnhancedCti'))
  const showTeamsEnhanced = useSelector(s => selectConfigProp(s, 'teamsEnhanced'))
  const dispatch = useDispatch()
  const electron = useSelector(selectInIpfxClient)

  if (!myId || !(electron && access.isTeamsPbx) || myTeamsEnhancedCti === undefined || !showTeamsEnhanced) return null

  return <Toggle value={myTeamsEnhancedCti} onChange={c => dispatch(actions.updateEnhancedIntegration(myId.id, c))}>
    Enhanced Deliver Integration
  </Toggle>
}

function AwayFromDeskTimeoutSettings() {
  const dispatch = useDispatch()
  const awayFromDeskSeconds = useSelector(selectAwayFromDeskTimeoutSeconds)
  const electron = useSelector(selectInIpfxClient)

  if (!electron) return null

  return <div className='awayFromDeskContainer'>
    <PresenceAwayTimeout className='svg'/>
    Away after
    <select id='away-from-desk-timeout-select' value={awayFromDeskSeconds} onClick={e => {
      e.preventDefault()
      e.stopPropagation()
    }}
      onChange={(e) => {
        dispatch(actions.setAwayFromDeskTimeout(parseInt(e.currentTarget.value)))
      }}>
      <option key='never' value={-1}>Never</option>
      <option key='1min' value={60}>1 minute</option>
      <option key='2min' value={120}>2 minutes</option>
      <option key='3min' value={180}>3 minutes</option>
      <option key='4min' value={240}>4 minutes</option>
      <option key='5min' value={300}>5 minutes</option>
      <option key='10min' value={600}>10 minutes</option>
      <option key='15min' value={900}>15 minutes</option>
      <option key='20min' value={1200}>20 minutes</option>
      <option key='30min' value={1800}>30 minutes</option>
      <option key='45min' value={2700}>45 minutes</option>
      <option key='1hr' value={3600}>1 hour</option>
    </select>
  </div>
}

function ClientLogging() {
  const value = useSelector(selectClientLogging)
  const dispatch = useDispatch()

  return <Toggle value={value} onChange={c => dispatch(actions.setClientLogging(!value))}>
    Client Logging
  </Toggle>
}

function InstantMessageSuppressWithPresence() {
  const settings = useSelector(selectInstantMessagerSettings)
  const dispatch = useDispatch()

  if (!settings) return null

  return <Toggle value={settings.imBlockMessageInLocation} onChange={c => dispatch(actions.instantMessagerSettingsSet(undefined, undefined, c))}>
    Suppress IM when presence is set
  </Toggle>
}

function InstantMessageSendEmailWhenUnavaliable() {
  return null
  /*
  const settings = useSelector(selectInstantMessagerSettings)
  const dispatch = useDispatch()
 
 
  if (!settings) return null
 
return <Toggle value={settings.imAllowBlockedMessagesSentAsEmails} onChange={c => dispatch(actions.instantMessagerSettingsSet(undefined, c, undefined))}>
    Allow receipt of IM as Email when Unavailable
</Toggle>
  */
}


function ShowQueueAgentsButton() {
  const dispatch = useDispatch()
  const show = useSelector(selectShowQueueAgents)

  return <MenuItem onClick={() => dispatch(actions.setShowQueueAgents(!show))}>
    <ShowAgentsIcon />
    {show ? 'Hide queue agents' : 'Show queue agents'}
  </MenuItem>
}


function ShowQueuingMediaButton() {
  const dispatch = useDispatch()
  const show = useSelector(selectShowQueuingMedia)

  return <MenuItem onClick={() => dispatch(actions.setShowQueuingMedia(!show))}>
    <ShowMediaIcon />
    {show ? 'Hide queue media' : 'Show queue media'}
  </MenuItem>
}

function ShowQueueGridFixedButton() {
  const dispatch = useDispatch()
  const height = useSelector(selectFixedQueueGridHeights)

  return <MenuItem itemClass={'item-special'}>
    <h4 >Agents &amp; media display length</h4>
    <select id='fixed-heights' value={height} onClick={e => {
      e.preventDefault()
      e.stopPropagation()
    }}
      onChange={(e) => {
        dispatch(actions.setFixedQueueGridHeight(parseFloat(e.currentTarget.value)))
      }}>
      <option key={'short'} value={0.25}>Short</option>
      <option key={'medium'} value={0.5}>Medium</option>
      <option key={'large'} value={1}>Long</option>
      <option key={'default'} value={0}>Full (Default)</option>
    </select>
  </MenuItem>
}

function CompactViewButton() {
  const compactView = useSelector(selectCompactView)
  const dispatch = useDispatch()

  return <Toggle value={compactView} onChange={c => dispatch(actions.setCompactView(c))}>
    Compact view
  </Toggle>
}

function VitrualQueueButton() {
  const vitrualQueueView = useSelector(s => selectApplicationSetting(s, 'ShowVirtualQueues'))
  const dispatch = useDispatch()

  if (!vitrualQueueView) return null

  return <Toggle value={vitrualQueueView.value === 'true'} onChange={c => dispatch(actions.setVirtualQueueView(c))}>
    Virtual queues
  </Toggle>
}

function ExtensionPresenceExpiryButton() {
  const extensionPresenceExpiry = useSelector(s => selectApplicationSetting(s, 'ExtensionPresenceExpiry'))
  const dispatch = useDispatch()

  if (!extensionPresenceExpiry) return null

  return <Toggle value={extensionPresenceExpiry.value === 'true'} onChange={c => dispatch(actions.setExtensionPresenceExpiry(c))}>
    Presence Expiry Prompt
  </Toggle>
}

function OpenAgentViewOnAnswer() {
  const value = useSelector(s => selectApplicationSetting(s, 'AgentViewOnAnswer'))
  const dispatch = useDispatch()

  if (!value) return null

  return <Toggle value={value.value === 'true'} onChange={c => dispatch(actions.setAgentViewOnAnswer(c))}>
    Open agent view on answer
  </Toggle>
}

function AutoAnswerButton() {
  const autoAnswer = useSelector(s => selectApplicationSetting(s, 'ExtensionAutoAnswer'))
  const dispatch = useDispatch()

  if (!autoAnswer) return null

  return <Toggle value={autoAnswer.value === 'true'} onChange={c => dispatch(actions.setExtensionAutoAnswer(c))}>
    Auto Answer Queue Calls
  </Toggle>
}

function OpenCrmButton() {
  const settings = useSelector(selectCrmSettings)

  return <MenuItem onClick={() => openWindowCrmLink(settings.defaultCrmPage)}>
    <Dynamics365Icon />
    Open Dynamics 365
  </MenuItem>
}

function ThemeButton() {
  const dispatch = useDispatch()
  const { inTeams, theme } = useTeams()
  if (inTeams) { return null }

  const themes: Theme[] = ['light', 'dark', 'contrast']

  return <MenuItem subMenu={<SubMenu>
    {themes.map(t =>
      <MenuItem key={t} onClick={() => {
        //setTheme(t)
        dispatch(actions.setTheme(t))
      }} noIcon={theme !== t}>
        {theme === t && <CheckIcon />}
        {t[0].toUpperCase() + t.substr(1)}
      </MenuItem>)}
  </SubMenu>}>
    <ThemeIcon /> Theme
  </MenuItem>
}


